import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  Announcement,
  Warning,
  CheckCircle,
  Assignment,
  Save,
  Refresh,
  History,
  Close,
  Person,
  AccessTime,
} from '@mui/icons-material';
import { announcementAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface AnnouncementData {
  id?: number;
  maintenance_notice: string;
  new_features: string;
  usage_reminder: string;
  user_id?: number;
  user_name?: string;
  created_at?: string;
  created_at_local?: string;
}

const AnnouncementManagePage: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<AnnouncementData>({
    maintenance_notice: '',
    new_features: '',
    usage_reminder: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const [historyDialog, setHistoryDialog] = useState(false);
  const [historyData, setHistoryData] = useState<AnnouncementData[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await announcementAPI.getAnnouncements();
      if (response && response.id) {
        setFormData(response);
      }
    } catch (error) {
      showSnackbar('載入公告失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    // 檢查字數限制
    if (formData.maintenance_notice.length > 255) {
      showSnackbar('系統維護通知超過255字限制', 'error');
      return;
    }
    if (formData.new_features.length > 255) {
      showSnackbar('新功能超過255字限制', 'error');
      return;
    }
    if (formData.usage_reminder.length > 255) {
      showSnackbar('使用提醒超過255字限制', 'error');
      return;
    }

    // 顯示確認對話框
    setConfirmDialog(true);
  };

  const handleConfirmSave = async () => {
    setConfirmDialog(false);
    setLoading(true);
    try {
      await announcementAPI.createAnnouncement(formData);
      showSnackbar('公告儲存成功', 'success');
      // 清空表單
      setFormData({
        maintenance_notice: '',
        new_features: '',
        usage_reminder: '',
      });
    } catch (error) {
      showSnackbar('公告儲存失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      maintenance_notice: '',
      new_features: '',
      usage_reminder: '',
    });
    showSnackbar('已重置公告內容', 'info');
  };

  const handleViewHistory = async () => {
    setHistoryDialog(true);
    setHistoryLoading(true);
    try {
      const response = await announcementAPI.getHistory();
      setHistoryData(response);
    } catch (error) {
      showSnackbar('載入歷史公告失敗', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!user || !user.permission || Number(user.permission) < 100) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', mt: 5 }}>
        <Typography variant="h5" color="error">
          您沒有權限訪問此頁面。
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>

      <Box display="flex" flexDirection="column" gap={3}>
        {/* 系統維護通知 */}
        <Card sx={{ 
          p: 3, 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '2px solid #ff6b35',
          backgroundColor: '#fff5f5'
        }}>
          <Box display="flex" gap={3}>
            {/* 左側標題區域 */}
            <Box sx={{ minWidth: 200, flexShrink: 0 }}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Warning sx={{ color: '#ff6b35', fontSize: '1.5rem' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                  系統維護通知
                </Typography>
              </Box>
              <Typography variant="caption" color={formData.maintenance_notice.length > 255 ? 'error' : 'text.secondary'} sx={{ fontSize: '1rem' }}>
                字數: {formData.maintenance_notice.length}/255
              </Typography>
            </Box>
            
            {/* 右側輸入框區域 */}
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="維護通知內容"
                value={formData.maintenance_notice}
                onChange={(e) => setFormData(prev => ({ ...prev, maintenance_notice: e.target.value }))}
                placeholder="請輸入系統維護通知內容..."
                variant="outlined"
                inputProps={{ maxLength: 255 }}
                error={formData.maintenance_notice.length > 255}
                helperText={formData.maintenance_notice.length > 255 ? '超過255字限制' : ''}
              />
            </Box>
          </Box>
        </Card>

        {/* 新功能 */}
        <Card sx={{ 
          p: 3, 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '2px solid #2196f3',
          backgroundColor: '#f3f8ff'
        }}>
          <Box display="flex" gap={3}>
            {/* 左側標題區域 */}
            <Box sx={{ minWidth: 200, flexShrink: 0 }}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <CheckCircle sx={{ color: '#2196f3', fontSize: '1.5rem' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  新功能
                </Typography>
              </Box>
              <Typography variant="caption" color={formData.new_features.length > 255 ? 'error' : 'text.secondary'} sx={{ fontSize: '1rem' }}>
                字數: {formData.new_features.length}/255
              </Typography>
            </Box>
            
            {/* 右側輸入框區域 */}
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="新功能內容"
                value={formData.new_features}
                onChange={(e) => setFormData(prev => ({ ...prev, new_features: e.target.value }))}
                placeholder="請輸入新功能介紹內容..."
                variant="outlined"
                inputProps={{ maxLength: 255 }}
                error={formData.new_features.length > 255}
                helperText={formData.new_features.length > 255 ? '超過255字限制' : ''}
              />
            </Box>
          </Box>
        </Card>

        {/* 使用提醒 */}
        <Card sx={{ 
          p: 3, 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '2px solid #4caf50',
          backgroundColor: '#f1f8e9'
        }}>
          <Box display="flex" gap={3}>
            {/* 左側標題區域 */}
            <Box sx={{ minWidth: 200, flexShrink: 0 }}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Assignment sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                  使用提醒
                </Typography>
              </Box>
              <Typography variant="caption" color={formData.usage_reminder.length > 255 ? 'error' : 'text.secondary'} sx={{ fontSize: '1rem' }}>
                字數: {formData.usage_reminder.length}/255
              </Typography>
            </Box>
            
            {/* 右側輸入框區域 */}
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="使用提醒內容"
                value={formData.usage_reminder}
                onChange={(e) => setFormData(prev => ({ ...prev, usage_reminder: e.target.value }))}
                placeholder="請輸入使用提醒內容..."
                variant="outlined"
                inputProps={{ maxLength: 255 }}
                error={formData.usage_reminder.length > 255}
                helperText={formData.usage_reminder.length > 255 ? '超過255字限制' : ''}
              />
            </Box>
          </Box>
        </Card>

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button
            variant="outlined"
            startIcon={<History />}
            onClick={handleViewHistory}
            disabled={loading}
            sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold', borderRadius: 2 }}
          >
            查看歷史公告
          </Button>
          
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleReset}
              disabled={loading}
              sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold', borderRadius: 2 }}
            >
              重置
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={loading || formData.maintenance_notice.length > 255 || formData.new_features.length > 255 || formData.usage_reminder.length > 255}
              sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold', borderRadius: 2 }}
            >
              儲存公告
            </Button>
          </Box>
        </Box>
      </Box>

      {/* 確認儲存對話框 */}
      <Dialog 
        open={confirmDialog} 
        onClose={() => setConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
          color: 'white',
          textAlign: 'center',
          fontSize: '1.5rem',
          fontWeight: 'bold',
          py: 3
        }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
            <Save sx={{ fontSize: '2rem' }} />
            確認儲存公告
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4, bgcolor: '#fafafa' }}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', color: '#333' }}>
              確定要儲存此公告嗎？
            </Typography>
            
            <Typography variant="body1" sx={{ textAlign: 'center', color: '#666', lineHeight: 1.6 }}>
              儲存後，此公告將會立即生效並顯示在儀表板上。
              <br />
              所有用戶在下次進入儀表板時都會看到此公告。
            </Typography>

            {/* 顯示即將儲存的內容預覽 */}
            <Box sx={{ 
              p: 3, 
              bgcolor: 'white', 
              borderRadius: 2, 
              border: '1px solid #e0e0e0',
              maxHeight: 300,
              overflow: 'auto'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
                公告內容預覽：
              </Typography>
              
              {formData.maintenance_notice && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6b35', mb: 1 }}>
                    系統維護通知：
                  </Typography>
                  <Typography variant="body2" sx={{ pl: 2, color: '#666' }}>
                    {formData.maintenance_notice}
                  </Typography>
                </Box>
              )}
              
              {formData.new_features && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2196f3', mb: 1 }}>
                    新功能：
                  </Typography>
                  <Typography variant="body2" sx={{ pl: 2, color: '#666' }}>
                    {formData.new_features}
                  </Typography>
                </Box>
              )}
              
              {formData.usage_reminder && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50', mb: 1 }}>
                    使用提醒：
                  </Typography>
                  <Typography variant="body2" sx={{ pl: 2, color: '#666' }}>
                    {formData.usage_reminder}
                  </Typography>
                </Box>
              )}
              
              {!formData.maintenance_notice && !formData.new_features && !formData.usage_reminder && (
                <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                  無公告內容
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, bgcolor: '#f5f5f5', justifyContent: 'center', gap: 2 }}>
          <Button 
            onClick={() => setConfirmDialog(false)}
            variant="outlined"
            size="large"
            sx={{ 
              px: 4, 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2
            }}
          >
            取消
          </Button>
          <Button 
            onClick={handleConfirmSave}
            variant="contained"
            size="large"
            sx={{ 
              px: 4, 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
              }
            }}
          >
            確定儲存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 歷史公告對話框 */}
      <Dialog 
        open={historyDialog} 
        onClose={() => setHistoryDialog(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            maxWidth: '95vw',
            width: '95vw'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <History sx={{ color: '#1976d2', fontSize: '1.5rem' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              歷史公告記錄
            </Typography>
          </Box>
          <Button
            onClick={() => setHistoryDialog(false)}
            startIcon={<Close />}
            sx={{ color: '#666' }}
          >
            關閉
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600, width: '100%' }}>
              <Table stickyHeader sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      color: 'white !important', 
                      fontWeight: 'bold',
                      backgroundColor: '#1976d2 !important',
                      border: '1px solid #1565c0 !important',
                      fontSize: '1.2rem',
                      textAlign: 'center'
                    }}>編號</TableCell>
                    <TableCell sx={{ 
                      color: 'white !important', 
                      fontWeight: 'bold',
                      backgroundColor: '#1976d2 !important',
                      border: '1px solid #1565c0 !important',
                      fontSize: '1.2rem',
                      textAlign: 'center'
                    }}>系統維護通知</TableCell>
                    <TableCell sx={{ 
                      color: 'white !important', 
                      fontWeight: 'bold',
                      backgroundColor: '#1976d2 !important',
                      border: '1px solid #1565c0 !important',
                      fontSize: '1.2rem',
                      textAlign: 'center'
                    }}>新功能</TableCell>
                    <TableCell sx={{ 
                      color: 'white !important', 
                      fontWeight: 'bold',
                      backgroundColor: '#1976d2 !important',
                      border: '1px solid #1565c0 !important',
                      fontSize: '1.2rem',
                      textAlign: 'center'
                    }}>使用提醒</TableCell>
                    <TableCell sx={{ 
                      color: 'white !important', 
                      fontWeight: 'bold',
                      backgroundColor: '#1976d2 !important',
                      border: '1px solid #1565c0 !important',
                      fontSize: '1.2rem',
                      textAlign: 'center'
                    }}>建立者</TableCell>
                    <TableCell sx={{ 
                      color: 'white !important', 
                      fontWeight: 'bold',
                      backgroundColor: '#1976d2 !important',
                      border: '1px solid #1565c0 !important',
                      fontSize: '1.2rem',
                      textAlign: 'center'
                    }}>建立時間</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.map((announcement) => (
                    <TableRow key={announcement.id} hover>
                      <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '1.2rem' }}>{announcement.id}</TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0', minWidth: 300 }}>
                        <Typography variant="body2" sx={{ 
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap',
                          fontSize: '1.2rem'
                        }}>
                          {announcement.maintenance_notice || '無'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0', minWidth: 300 }}>
                        <Typography variant="body2" sx={{ 
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap',
                          fontSize: '1.2rem'
                        }}>
                          {announcement.new_features || '無'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0', minWidth: 300 }}>
                        <Typography variant="body2" sx={{ 
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap',
                          fontSize: '1.2rem'
                        }}>
                          {announcement.usage_reminder || '無'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person sx={{ fontSize: '1.2rem', color: '#666' }} />
                          <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
                            {announcement.user_name || `用戶 ${announcement.user_id}`}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccessTime sx={{ fontSize: '1.2rem', color: '#666' }} />
                          <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
                            {announcement.created_at_local || (announcement.created_at ? formatDate(announcement.created_at) : '未知')}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* 載入覆蓋層 */}
      {loading && (
        <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="inherit" />
            <Typography variant="h6">處理中...</Typography>
          </Box>
        </Backdrop>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AnnouncementManagePage;